import {AugmentedDashboardWidgetEntry, DashboardWidgetEntry, DashboardWidgetLayoutConfig, DashboardWidgetType, DashboardWidgetVariantInfo} from "../../types";
import React, {FunctionComponent, useCallback, useEffect, useMemo, useState} from "react";
import {DashboardWidgetConfig, WidgetOriginalParameter} from "../../dashboard-widget.config";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import {BarChartRounded, BorderAllRounded, CallToActionRounded, CloseRounded, StackedLineChartRounded, ViewQuiltRounded} from "@mui/icons-material";
import {deepEqual} from "../../../../../../../../core/services/utils/utils";
import {Box, Checkbox, FormControlLabel, IconButton, Paper, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import SaveOrRollback from "../../../../../../../components/app-specific/save-or-rollback";
// @ts-ignore
import {ReactComponent as CheckboxIcon} from "../../../../../../../../assets/images/check-box/checkbox.svg";
// @ts-ignore
import {ReactComponent as ActiveCheckboxIcon} from "../../../../../../../../assets/images/check-box/checkbox-active.svg";


export interface DashboardWidgetSettingsProps {
    widget: AugmentedDashboardWidgetEntry,
    saveDashboardWidgetConfig?: (connectionId: DashboardWidgetEntry['connectionId'], config: DashboardWidgetLayoutConfig) => Promise<boolean>,
    toggleViewSettings?: () => void,
    savingDashboardWidgetConfig?: boolean,
}

export const DashboardWidgetSettings: FunctionComponent<DashboardWidgetSettingsProps> = ({widget, saveDashboardWidgetConfig, toggleViewSettings, savingDashboardWidgetConfig}) => {
    const [localType, setLocalType] = useState<DashboardWidgetVariantInfo>();
    const [_visibleParameters, setVisibleParameters] = useState(widget?.underlyingDataVisibility);
    const [savingLocalTypeChange, setSavingLocalTypeChange] = useState(false);
    const canSaveConfig = Boolean(saveDashboardWidgetConfig);

    const config = useMemo(() => new DashboardWidgetConfig(widget?.id, localType?.variant ?? widget?.variant, localType?.type ?? widget?.type), [widget?.id, localType?.variant, localType?.type, widget?.variant, widget?.type]);

    const variants = useMemo(() => config.variants, [config.variants]);
    const widgetParameters = useMemo(() => config.underlyingParameters, [config.underlyingParameters]);
    const widgetParameterObjects = useMemo(() => {
        const paramObjects = config.parameters;
        return widgetParameters.map(paramName => paramObjects.find(p => p.fieldName === paramName)).filter(p => !!p) as WidgetOriginalParameter[];
    }, [config.parameters, widgetParameters]);

    const isMounted = useIsMounted();

    const visibleParameters = useMemo(() => {
        return widgetParameters.reduce((acc, param) => {
            acc[param] = _visibleParameters?.[param] ?? true; // Default to true if not set
            return acc;
        }, {} as Record<string, boolean>);
    }, [_visibleParameters, widgetParameters]);

    const defaultVariantAndType = useMemo(() => {
        let found = variants.find(e =>
            e.type === widget?.type &&
            ((e.variant === widget?.variant) || (
                (e.variant === undefined && widget?.variant === undefined) ||
                (e.variant === null && widget?.variant === null) ||
                (e.variant === undefined && widget?.variant === null) ||
                (e.variant === null && widget?.variant === undefined)
            ))
        );
        if (!found) {
            found = variants.find(e => e.isDefaultVariant);
        }
        return found;
    }, [variants, widget?.type, widget?.variant]);

    /**
     * When the widget type changes, update the local type
     */
    useEffect(() => setLocalType(defaultVariantAndType), [defaultVariantAndType])

    const compatibleVariantList = useMemo(() => {
        const typeIcons = {
            [DashboardWidgetType.table]: <BorderAllRounded fontSize={'small'}/>,
            [DashboardWidgetType.lineChart]: <StackedLineChartRounded fontSize={'small'}/>,
            [DashboardWidgetType.barChart]: <BarChartRounded fontSize={'small'}/>,
            [DashboardWidgetType.infoBlock]: <CallToActionRounded fontSize={'small'}/>,
            [DashboardWidgetType.treeMap]: <ViewQuiltRounded fontSize={'small'}/>,
            // TODO: add more widget types
        }
        const typeLabelSuffixes = {
            [DashboardWidgetType.table]: 'Table',
            [DashboardWidgetType.lineChart]: 'Line Chart',
            [DashboardWidgetType.barChart]: 'Bar Chart',
            [DashboardWidgetType.infoBlock]: 'Info Block',
            [DashboardWidgetType.treeMap]: 'Tree Map',
            // TODO: add more widget types
        }
        return variants.map(e => ({
            value: e,
            icon: typeIcons[e.type],
            label: `${e.variant ? e.variant + " " : ""}${typeLabelSuffixes[e.type] ?? 'Unknown Type'}`,
        }))
    }, [variants])

    const visibleParamsChanged = useMemo(() => {
        return !deepEqual(visibleParameters, widget?.underlyingDataVisibility);
    }, [visibleParameters, widget?.underlyingDataVisibility]);

    const variantChanged = useMemo(() => {
        return !deepEqual(localType ? {type: localType.type, variant: localType.variant} : localType, {type: widget?.type, variant: widget?.variant ?? undefined});
    }, [localType, widget?.type, widget?.variant]);

    /**
     * Rollback changes to the widget configuration
     */
    const rollbackChanges = useCallback(() => {
        if (visibleParamsChanged) {
            setVisibleParameters(widget?.underlyingDataVisibility);
        }
        if (variantChanged) {
            setLocalType(defaultVariantAndType);
        }
    }, [variantChanged, visibleParamsChanged, widget?.underlyingDataVisibility, defaultVariantAndType]);

    /**
     * Save changes to the widget configuration, including visible parameters and type change
     */
    const saveChanges = useCallback(async () => {
        setSavingLocalTypeChange(true);
        const resultFlag = await saveDashboardWidgetConfig?.(widget?.connectionId, {
            size: widget?.size,
            orderIndex: widget?.orderIndex,
            type: localType?.type ?? widget?.type,
            variant: localType?.variant,
            underlyingDataVisibility: visibleParameters ?? widget?.underlyingDataVisibility,
        });
        if (isMounted()) {
            setSavingLocalTypeChange(false);
        }
        if (resultFlag) {
            toggleViewSettings?.();
        }
        return resultFlag ?? false;
    }, [isMounted, localType?.type, localType?.variant, saveDashboardWidgetConfig, toggleViewSettings, visibleParameters, widget?.connectionId, widget?.orderIndex, widget?.size, widget?.type, widget?.underlyingDataVisibility])

    return (
        <>
            <Box sx={{height: '100%', display: 'flex', flexDirection: 'column',}}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 1,}}>
                    <Grid2 container spacing={1} alignItems="flex-start" justifyContent={'space-between'} width={'100%'}>
                        <Grid2 xs={12} md>
                            <Typography variant="body1" fontWeight={600}>
                                Configurations for {widget?.customTitle ?? widget?.title ?? ""}
                            </Typography>
                        </Grid2>
                        <Grid2 xs={12} md={'auto'} display={'flex'} justifyContent={'flex-end'}>
                            <Box sx={{display: 'flex', gap: 0.5}}>
                                <Tooltip title="Close Settings">
                                    <Box component={'span'}>
                                        <IconButton
                                            size="small"
                                            color={'paper.error'}
                                            onClick={toggleViewSettings}
                                        >
                                            <CloseRounded/>
                                        </IconButton>
                                    </Box>
                                </Tooltip>
                            </Box>
                        </Grid2>
                    </Grid2>
                </Box>
                <Paper sx={{padding: 1, marginInline: 1, marginBlockStart: 3, flexGrow: 1, position: 'relative', display: 'flex', flexDirection: 'column', maxHeight: 'fit-content',}}>
                    <Grid2 container spacing={2}>
                        <Grid2 xs={12}>
                            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} marginBottom={1} sx={{minHeight: 37}}>
                                <Typography variant={'body2'} fontWeight={600}>
                                    Available variants:
                                </Typography>
                                {
                                    canSaveConfig && (visibleParamsChanged || variantChanged) &&
                                    <SaveOrRollback
                                        show
                                        showText
                                        column={false}
                                        loading={savingDashboardWidgetConfig && savingLocalTypeChange}
                                        onRollback={() => rollbackChanges()}
                                        onSave={() => saveChanges()}
                                    />
                                }
                            </Stack>
                            <ToggleButtonGroup
                                disabled={savingDashboardWidgetConfig || !canSaveConfig}
                                size={'small'}
                                exclusive
                                value={localType}
                                onChange={(_, type) => type && setLocalType(type)}
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                }}
                            >
                                {
                                    compatibleVariantList.map((type) => (
                                        <Tooltip
                                            key={`${type.value.type}-${type.value.variant}`}
                                            title={type.value === localType ? "Currently shown" : "Change to " + type.label}
                                            placement={'top'}
                                        >
                                            <Box component={'span'}>
                                                <ToggleButton
                                                    value={type.value}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        fontSize: 'small',
                                                        margin: '0px !important',
                                                    }}
                                                >
                                                    {type.icon}
                                                    <Typography noWrap variant={'body2'}>
                                                        {type.label}
                                                    </Typography>
                                                </ToggleButton>
                                            </Box>
                                        </Tooltip>
                                    ))
                                }
                            </ToggleButtonGroup>
                        </Grid2>
                        <Grid2 xs={12}>
                            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{minHeight: 37}}>
                                <Typography variant={'body2'} fontWeight={600}>
                                    Visible parameters / axes:
                                </Typography>
                            </Stack>
                            <Grid2 container spacing={1} columns={12}>
                                {
                                    widgetParameterObjects.map((paramObject) => (
                                        <Grid2 key={paramObject.fieldName} xs={'auto'}>
                                            <FormControlLabel
                                                sx={{margin: 0}}
                                                label={<Typography variant={'body2'}>{paramObject.title}</Typography>}
                                                control={
                                                    <Checkbox
                                                        disabled={!canSaveConfig}
                                                        checked={visibleParameters[paramObject.fieldName]}
                                                        icon={<CheckboxIcon/>}
                                                        checkedIcon={<ActiveCheckboxIcon/>}
                                                        onChange={(e) => setVisibleParameters({
                                                            ...visibleParameters,
                                                            [paramObject.fieldName]: e.target.checked,
                                                        })}
                                                    />
                                                }
                                            />
                                        </Grid2>
                                    ))
                                }
                            </Grid2>
                        </Grid2>
                    </Grid2>
                </Paper>
            </Box>
        </>
    );
}